import { zodResolver } from "@hookform/resolvers/zod";
import { RadioGroup, FormControlLabel, Radio, Button } from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";
import type z from "zod";

import CounterButton from "@/components/CounterButton/CounterButton";
import { cabinClassLabelKeysEntries } from "@/features/flights/utils/cabinClasses";
import { flightSearchFormInputSchema } from "@/features/flights/utils/flightsValidation";
import { MAX_TRAVELLERS } from "@/utils/constants";

const travellersFormSchema = flightSearchFormInputSchema.pick({
  adult: true,
  child: true,
  infantWithoutSeat: true,
  cabinClass: true,
});
type TravellersFormValues = z.infer<typeof travellersFormSchema>;

type TravellersSelectionProps = {
  initialValues: TravellersFormValues;
  onConfirm: (values: TravellersFormValues) => void;
};

export function TravellersSelection({
  initialValues,
  onConfirm,
}: TravellersSelectionProps) {
  const t = useTranslations();

  const { control, handleSubmit } = useForm({
    defaultValues: initialValues,
    resolver: zodResolver(travellersFormSchema),
  });

  const [adult, child] = useWatch({ control, name: ["adult", "child"] });

  const maximumChild = MAX_TRAVELLERS - adult;
  const maximumAdult = MAX_TRAVELLERS - child;

  return (
    <form
      onSubmit={(event) => {
        event.stopPropagation();
        handleSubmit(onConfirm)(event);
      }}
      className="flex h-full w-full flex-col"
    >
      <span className="mb-4 inline-block text-xs text-text-tertiary">
        {t("flights.search.travellers")}
      </span>

      <TravellerTypeItemWrapper
        label={t("flights.search.adult")}
        ariaLabel="Adult"
        description={t("flights.search.twelveYearOldPlus")}
      >
        <Controller
          name="adult"
          control={control}
          render={({ field }) => (
            <CounterButton {...field} minValue={1} maxValue={maximumAdult} />
          )}
        />
      </TravellerTypeItemWrapper>

      <TravellerTypeItemWrapper
        label={t("flights.search.child")}
        ariaLabel="Child"
        description={t("flights.search.twoToTwelveYearOld")}
      >
        <Controller
          name="child"
          control={control}
          render={({ field }) => (
            <CounterButton
              {...field}
              value={field.value}
              maxValue={maximumChild}
            />
          )}
        />
      </TravellerTypeItemWrapper>

      <TravellerTypeItemWrapper
        label={t("flights.search.infant")}
        ariaLabel="Infant"
        description={t("flights.search.withoutSeat")}
      >
        <Controller
          name="infantWithoutSeat"
          control={control}
          render={({ field }) => <CounterButton {...field} maxValue={adult} />}
        />
      </TravellerTypeItemWrapper>

      <span className="mt-4 mb-2 inline-block text-xs text-text-tertiary">
        {t("flights.cabinClass.cabinClass")}
      </span>

      <div>
        <Controller
          control={control}
          name="cabinClass"
          render={({ field }) => (
            <RadioGroup
              aria-labelledby="cabin-class-radio-group"
              defaultValue="economy"
              className="space-y-4!"
              {...field}
            >
              {cabinClassLabelKeysEntries.map(([value, labelKey]) => (
                <FormControlLabel
                  key={value}
                  className="justify-between"
                  value={value}
                  control={<Radio />}
                  label={t(labelKey)}
                  labelPlacement="start"
                />
              ))}
            </RadioGroup>
          )}
        />
      </div>

      <Button
        variant="contained"
        size="medium"
        type="submit"
        fullWidth
        sx={{
          marginTop: "auto",
        }}
      >
        {t("confirm")}
      </Button>
    </form>
  );
}

type TravellerTypeItemWrapperProps = {
  label: string;
  ariaLabel: string;
  description: string;
  children: React.ReactNode;
};

function TravellerTypeItemWrapper({
  label,
  ariaLabel,
  description,
  children,
}: TravellerTypeItemWrapperProps) {
  return (
    <div className="flex justify-between py-2" aria-label={ariaLabel}>
      <div className="flex flex-col">
        <h4 className="font-medium text-text-primary">{label}</h4>

        <span className="inline-block max-w-50 text-xs font-normal text-wrap text-text-tertiary">
          {description}
        </span>
      </div>

      {children}
    </div>
  );
}
